import { getMockDataAdapter } from '@ascend/mocked-shared/mock-data';

type Claim = { type: string; issuer: string; value: string; issuedAt: string };
type OnchainIdShape = { id: string; address: string; claims: Claim[]; source: 'ascend' | 'external' | 'erc3643' };

type FacilityShape = {
  id: string;
  name: string;
  settlementTokens: string[];
  collateralTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  tvl: string;
  borrowed: string;
  status: 'active' | 'paused';
  region: string;
  category: string;
};

type MarginAccountShape = {
  accountId: string;
  facilityId: string;
  facilityName: string;
  settlementToken: string;
  collateralTokens: string[];
  collateralTokenText: string;
  collateralAmounts: Record<string, string>;
  collateralAmountText: string;
  collateralValue: string;
  collateralValueUsd: number;
  borrowedAmount: string;
  borrowedValue: string;
  borrowedUsd: number;
  interestAccrued: string;
  interestAccruedUsd: number;
  interestRatePct: number;
  liquidationThresholdPct: number;
  maxLtvPct: number;
  healthFactor: string;
  status: 'healthy' | 'at-risk' | 'liquidated';
};

function fmtCurrency(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

function fmtAmount(value: number): string {
  if (value >= 1000 || Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function toFacilityStatus(status: string): 'active' | 'paused' {
  return status === 'paused' || status === 'shutdown' ? 'paused' : 'active';
}

function toAccountStatus(status: string): 'healthy' | 'at-risk' | 'liquidated' {
  if (status === 'liquidated') return 'liquidated';
  if (status === 'default' || status === 'warn' || status === 'frozen' || status === 'paused') return 'at-risk';
  return 'healthy';
}

const adapter = await getMockDataAdapter();

const [facilities, accounts, liquidationsQueue, liquidationsHistory, auctions, kycCases, users] = await Promise.all([
  adapter.facilities.list(),
  adapter.accounts.list(),
  adapter.liquidations.queue(),
  adapter.liquidations.history(),
  adapter.auctions.list(),
  adapter.kyc.listCases(),
  adapter.users.list(),
]);

const collateralByAccountEntries = await Promise.all(
  accounts.map(async (account) => [account.id, await adapter.accounts.collateralByAccountId(account.id)] as const),
);
const collateralByAccount = new Map(collateralByAccountEntries);

const requirementsByFacilityEntries = await Promise.all(
  facilities.map(async (facility) => [facility.id, await adapter.compliance.requirementsByFacility(facility.id)] as const),
);
const requirementsByFacility = new Map(requirementsByFacilityEntries);

const onchainIds = await adapter.compliance.onchainIdsByUser('usr-011');
const onchainWithClaims: OnchainIdShape[] = await Promise.all(
  onchainIds.map(async (oid) => ({
    id: oid.id,
    address: oid.address,
    source: oid.source,
    claims: (await adapter.compliance.claimsByOnchainId(oid.id)).map((claim) => ({
      type: claim.claimType,
      issuer: claim.issuer,
      value: claim.value,
      issuedAt: claim.issuedAt,
    })),
  })),
);

const userAccessFacilityIds = await adapter.compliance.facilityAccessByUser('usr-011');

const facilityById = new Map(facilities.map((facility) => [facility.id, facility]));

export const TOKEN_COLORS: Record<string, string> = {
  APX: 'bg-purple-100 text-purple-700',
  MSM: 'bg-orange-100 text-orange-700',
  SBC: 'bg-emerald-100 text-emerald-700',
  ABN: 'bg-blue-100 text-blue-700',
  FMM: 'bg-pink-100 text-pink-700',
  SNG: 'bg-cyan-100 text-cyan-700',
};

export const TOKEN_NAMES: Record<string, string> = {
  ABN: 'Ascend Bond Network',
  FMM: 'Fasanara Money Market',
  SNG: 'Singapore RE Fund',
  APX: 'Apex Capital Fund',
  MSM: 'Meridian Stable Fund',
  SBC: 'Silverback Capital',
};

export const CLIENT_FACILITIES: FacilityShape[] = facilities.map((facility) => ({
  id: facility.id,
  name: facility.name,
  settlementTokens: facility.settlementTokens,
  collateralTokens: facility.collateralTokens,
  interestRate: `${facility.interestRatePct.toFixed(1)}%`,
  maxLTV: `${Math.round(facility.maxLtvPct)}%`,
  liquidationThreshold: `${Math.round(facility.liquidationThresholdPct)}%`,
  utilization: `${facility.utilizationPct.toFixed(1)}%`,
  tvl: fmtCurrency(facility.tvlUsd),
  borrowed: fmtCurrency(facility.borrowedUsd),
  status: toFacilityStatus(facility.status),
  region: facility.region,
  category: facility.category,
}));

export const CLIENT_FACILITY_TVL: Record<string, string> = Object.fromEntries(
  CLIENT_FACILITIES.map((facility) => [facility.id, facility.tvl]),
);

export const CLIENT_FACILITY_BORROWED: Record<string, string> = Object.fromEntries(
  CLIENT_FACILITIES.map((facility) => [facility.id, facility.borrowed]),
);

export const CLIENT_MARGIN_ACCOUNTS: MarginAccountShape[] = accounts.map((account) => {
  const facility = facilityById.get(account.facilityId);
  const collaterals = collateralByAccount.get(account.id) ?? [];
  const collateralTokens = collaterals.map((entry) => entry.tokenSymbol);
  const collateralAmounts: Record<string, string> = Object.fromEntries(
    collaterals.map((entry) => [entry.tokenSymbol, fmtAmount(entry.amount)]),
  );
  const collateralAmountText = collaterals
    .map((entry) => `${fmtAmount(entry.amount)} ${entry.tokenSymbol}`)
    .join(', ');

  return {
    accountId: account.id,
    facilityId: account.facilityId,
    facilityName: facility?.name ?? account.facilityId,
    settlementToken: account.settlementToken,
    collateralTokens,
    collateralTokenText: collateralTokens.join(', '),
    collateralAmounts,
    collateralAmountText,
    collateralValue: fmtCurrency(account.collateralValueUsd),
    collateralValueUsd: account.collateralValueUsd,
    borrowedAmount: `${fmtAmount(account.borrowedUsd)} ${account.settlementToken}`,
    borrowedValue: fmtCurrency(account.borrowedUsd),
    borrowedUsd: account.borrowedUsd,
    interestAccrued: `${fmtAmount(account.interestAccrued)} ${account.settlementToken}`,
    interestAccruedUsd: account.interestAccrued,
    interestRatePct: facility?.interestRatePct ?? 0,
    liquidationThresholdPct: facility?.liquidationThresholdPct ?? 150,
    maxLtvPct: facility?.maxLtvPct ?? 67,
    healthFactor: account.healthFactor.toFixed(2),
    status: toAccountStatus(account.status),
  };
});

export interface FacilityMarginAccount {
  accountId: string;
  deposited: number;
  borrowed: number;
  healthFactor: number;
  availableCredit: number;
  interestRatePct: number;
  interestAccruedUsd: number;
  interestAccrued: string;
  liquidationThresholdPct: number;
  status: string;
}

export const CLIENT_MARGIN_ACCOUNTS_BY_FACILITY: Record<string, FacilityMarginAccount[]> =
  CLIENT_MARGIN_ACCOUNTS.reduce((acc, account) => {
    if (!acc[account.facilityId]) acc[account.facilityId] = [];
    const deposited = Number(account.collateralValue.replace(/[^0-9.]/g, ''));
    const borrowed = Number(account.borrowedValue.replace(/[^0-9.]/g, ''));
    const facility = facilities.find((item) => item.id === account.facilityId);
    const maxBorrow = deposited * ((facility?.maxLtvPct ?? 0) / 100);
    acc[account.facilityId].push({
      accountId: account.accountId,
      deposited,
      borrowed,
      healthFactor: Number(account.healthFactor),
      availableCredit: Math.max(0, Math.round(maxBorrow - borrowed)),
      interestRatePct: account.interestRatePct,
      interestAccruedUsd: account.interestAccruedUsd,
      interestAccrued: account.interestAccrued,
      liquidationThresholdPct: account.liquidationThresholdPct,
      status: account.status,
    });
    return acc;
  }, {} as Record<string, FacilityMarginAccount[]>);

export const CLIENT_BORROW_INFO: Record<string, { borrowed: number; available: number; maxBorrow: number; limitUsedPct: number }> =
  CLIENT_MARGIN_ACCOUNTS.reduce((acc, account) => {
    const facility = facilities.find((item) => item.id === account.facilityId);
    const collateralValue = Number(account.collateralValue.replace(/[^0-9.]/g, ''));
    const borrowed = Number(account.borrowedValue.replace(/[^0-9.]/g, ''));
    const maxBorrow = collateralValue * ((facility?.maxLtvPct ?? 0) / 100);
    const available = Math.max(0, maxBorrow - borrowed);
    const limitUsedPct = maxBorrow > 0 ? (borrowed / maxBorrow) * 100 : 0;

    acc[account.facilityId] = {
      borrowed,
      available,
      maxBorrow,
      limitUsedPct,
    };
    return acc;
  }, {} as Record<string, { borrowed: number; available: number; maxBorrow: number; limitUsedPct: number }>);

export const CLIENT_WALLET_BALANCES: Record<string, string> = {
  ABN: '2.5',
  SNG: '10,000',
  FMM: '500',
  APX: '0',
  MSM: '8,000',
  SBC: '12,500',
  USDC: '50,000',
  USDT: '10,000',
  DAI: '8,000',
};

export const CLIENT_FACILITY_REQUIREMENTS: Record<string, Array<{ type: 'external_kyc_claim' | 'internal_claim'; claimType: string; description: string }>> =
  Object.fromEntries(
    facilities.map((facility) => [
      facility.id,
      (requirementsByFacility.get(facility.id) ?? []).map((item) => ({
        type: item.requirementType,
        claimType: item.claimType,
        description: item.description,
      })),
    ]),
  );

export const CLIENT_WALLET_ONCHAIN_IDS: Record<'default' | 'no_ids' | 'no_claims', OnchainIdShape[]> = {
  default: onchainWithClaims,
  no_ids: [],
  no_claims: onchainWithClaims.length
    ? [
        {
          ...onchainWithClaims[0],
          id: 'oid-empty',
          claims: [],
        },
      ]
    : [],
};

export const CLIENT_USER_ACCESS_IDS = new Set(userAccessFacilityIds);

export const CLIENT_LIQUIDATION_POSITIONS = liquidationsQueue.map((liq, index) => {
  const facility = facilityById.get(liq.facilityId);
  const token = (facility?.collateralTokens[0] ?? 'ABN').toUpperCase();
  const profitAmount = Math.max(0, liq.collateralAmountUsd - liq.debtAmountUsd);
  const profitMargin = liq.debtAmountUsd > 0 ? (profitAmount / liq.debtAmountUsd) * 100 : 0;
  const minutes = Math.max(1, 60 * (index + 1));

  return {
    id: liq.id,
    accountId: liq.marginAccountId,
    facility: facility?.name ?? liq.facilityId,
    token,
    tokenColor: TOKEN_COLORS[token] ?? 'bg-gray-100 text-gray-700',
    collateralAmount: Number((liq.collateralAmountUsd / 10000).toFixed(4)),
    collateralValue: liq.collateralAmountUsd,
    outstandingDebt: liq.debtAmountUsd,
    healthFactor: liq.healthFactor,
    timeInDefault: liq.timeInDefault,
    timeInDefaultMinutes: minutes,
    profitable: profitAmount > 0,
    profitAmount,
    profitMargin,
    findersFee: Math.round(liq.debtAmountUsd * 0.004),
    feeGuaranteed: index % 2 === 0,
  };
});

export const CLIENT_DDF_AUCTIONS: Array<{
  id: string;
  facilityName: string;
  accountId: string;
  token: string;
  tokenFullName: string;
  tokenColor: string;
  collateralAmount: number;
  collateralValue: number;
  debtAmount: number;
  reservePrice: number;
  currentBid: number;
  timeRemaining: string;
  status: 'active' | 'ending-soon' | 'ended';
  bidCount: number;
  kycRequired: boolean;
  eligible: boolean;
}> = auctions.map((auction) => {
  const facility = facilityById.get(auction.facilityId);
  return {
    id: auction.id,
    facilityName: facility?.name ?? auction.facilityId,
    accountId: auction.marginAccountId,
    token: auction.tokenSymbol,
    tokenFullName: auction.tokenName,
    tokenColor: TOKEN_COLORS[auction.tokenSymbol] ?? 'bg-gray-100 text-gray-700',
    collateralAmount: auction.collateralAmount,
    collateralValue: auction.collateralValueUsd,
    debtAmount: auction.debtAmountUsd,
    reservePrice: auction.reservePriceUsd,
    currentBid: auction.currentBidUsd,
    timeRemaining: auction.timeRemaining,
    status: auction.status === 'ending_soon' ? 'ending-soon' : auction.status === 'settled' ? 'ended' : 'active',
    bidCount: auction.bidCount,
    kycRequired: Boolean((requirementsByFacility.get(auction.facilityId) ?? []).length),
    eligible: CLIENT_USER_ACCESS_IDS.has(auction.facilityId),
  };
});

// Lookup: margin account ID → auction(s) for that account
export const CLIENT_AUCTIONS_BY_MARGIN_ACCOUNT: Record<string, Array<{ id: string; status: string }>> =
  CLIENT_DDF_AUCTIONS.reduce((acc, auction) => {
    if (!acc[auction.accountId]) acc[auction.accountId] = [];
    acc[auction.accountId].push({ id: auction.id, status: auction.status });
    return acc;
  }, {} as Record<string, Array<{ id: string; status: string }>>);

export const CLIENT_LIQUIDATION_HISTORY_EVENTS = liquidationsHistory.map((liq, index) => {
  const facility = facilityById.get(liq.facilityId);
  const token = facility?.collateralTokens[0] ?? 'ABN';
  const dt = liq.executedAt ?? `2026-03-${String(Math.max(1, 22 - index)).padStart(2, '0')}`;
  return {
    id: `he-${String(index + 1).padStart(2, '0')}`,
    date: dt,
    type: index % 2 === 0 ? 'Permissionless' as const : 'DDF' as const,
    accountId: liq.marginAccountId,
    token,
    tokenColor: TOKEN_COLORS[token] ?? 'bg-gray-100 text-gray-700',
    collateralAmount: Number((liq.collateralAmountUsd / 10000).toFixed(2)),
    debtCovered: liq.debtAmountUsd,
    recoveryRate: liq.recoveryRatePct || Number((90 + index).toFixed(1)),
    status: 'Settled' as const,
  };
});

export const CLIENT_DDF_AUCTION_DETAILS: Record<string, {
  id: string;
  tokenName: string;
  tokenSymbol: string;
  facility: string;
  status: 'active' | 'ending_soon' | 'settled';
  collateralAmount: number;
  collateralValue: number;
  debtAmount: number;
  reservePrice: number;
  currentBid: number;
  bidCount: number;
  timeRemaining: string;
  settlementToken: string;
  timeline: Array<{ description: string; timestamp: string; bidder?: string; amount?: number; txHash?: string }>;
  kycRequired: boolean;
  bidderRegistryRequired: boolean;
  reservePriceBuyoutEnabled: boolean;
}> = Object.fromEntries(
  auctions.map((auction) => {
    const facility = facilityById.get(auction.facilityId);
    return [
      auction.id,
      {
        id: auction.id,
        tokenName: auction.tokenName,
        tokenSymbol: auction.tokenSymbol,
        facility: facility?.name ?? auction.facilityId,
        status: auction.status,
        collateralAmount: auction.collateralAmount,
        collateralValue: auction.collateralValueUsd,
        debtAmount: auction.debtAmountUsd,
        reservePrice: auction.reservePriceUsd,
        currentBid: auction.currentBidUsd,
        bidCount: auction.bidCount,
        timeRemaining: auction.timeRemaining,
        settlementToken: auction.settlementToken,
        timeline: [],
        kycRequired: Boolean((requirementsByFacility.get(auction.facilityId) ?? []).length),
        bidderRegistryRequired: false,
        reservePriceBuyoutEnabled: true,
      },
    ];
  }),
);

for (const auction of auctions) {
  const bids = await adapter.auctions.bidsByAuctionId(auction.id);
  const detail = CLIENT_DDF_AUCTION_DETAILS[auction.id];
  if (!detail) continue;
  detail.timeline = [
    {
      description: 'Auction created following liquidation trigger',
      timestamp: '22/03/2026, 19:56:05',
      txHash: bids[0]?.txHash,
    },
    ...bids.map((bid, idx) => ({
      description:
        idx === bids.length - 1
          ? `Current leading bid at $${bid.amountUsd.toLocaleString()}`
          : idx === 0
            ? `First bid placed at $${bid.amountUsd.toLocaleString()}`
            : `Bid increased to $${bid.amountUsd.toLocaleString()}`,
      timestamp: bid.timestamp,
      bidder: bid.bidderWallet,
      amount: bid.amountUsd,
      txHash: bid.txHash,
    })),
  ];
}

export function getClientAuctionDetail(auctionId: string) {
  return CLIENT_DDF_AUCTION_DETAILS[auctionId] ?? CLIENT_DDF_AUCTION_DETAILS[auctions[0]?.id ?? ''];
}

export const CLIENT_HISTORY = CLIENT_LIQUIDATION_HISTORY_EVENTS.map((event, idx) => ({
  id: `hist-${idx + 1}`,
  date: event.date.split('-').reverse().join('/'),
  accountId: event.accountId,
  token: event.token,
  tokenColor: event.tokenColor,
  qty: Number(event.collateralAmount.toFixed(2)),
  debtPaid: event.debtCovered,
  profitLoss: Math.round(event.debtCovered * 0.12),
  profitPercent: 12.0,
  status: idx % 3 === 0 ? 'pending' as const : 'claimed' as const,
  txHash: `0x${(idx + 1).toString(16).padStart(6, '0')}...${(idx + 100).toString(16)}`,
}));

export const CLIENT_FINDERS_PAYOUTS = CLIENT_HISTORY.slice(0, 8).map((item, idx) => ({
  id: `fp-${idx + 1}`,
  accountId: item.accountId,
  date: item.date,
  fee: Math.round(item.debtPaid * 0.004),
  status: idx % 2 === 0 ? 'paid' as const : 'pending' as const,
  received: idx % 2 === 0 ? Math.round(item.debtPaid * 0.004) : 0,
  total: Math.round(item.debtPaid * 0.004),
  txHash: item.txHash,
}));

export const CLIENT_SETTLEMENTS: Array<{
  id: string;
  accountId: string;
  token: string;
  tokenColor: string;
  amount: number;
  estimatedValue: number;
  claimDeadline: string;
  daysRemaining: number;
  status: 'claimable' | 'expiring-soon' | 'expired';
}> = auctions.map((auction, idx) => {
  const daysRemaining = Math.max(0, (idx % 7) + (auction.status === 'settled' ? 0 : 2));
  const status = daysRemaining === 0 ? 'expired' : daysRemaining <= 3 ? 'expiring-soon' : 'claimable';
  const deadline = new Date('2026-03-24T00:00:00Z');
  deadline.setUTCDate(deadline.getUTCDate() + daysRemaining);

  return {
    id: `stl-${String(idx + 1).padStart(3, '0')}`,
    accountId: auction.marginAccountId,
    token: auction.tokenSymbol,
    tokenColor: TOKEN_COLORS[auction.tokenSymbol] ?? 'bg-gray-100 text-gray-700',
    amount: Number(auction.collateralAmount.toFixed(4)),
    estimatedValue: auction.currentBidUsd || auction.reservePriceUsd,
    claimDeadline: deadline.toISOString().split('T')[0],
    daysRemaining,
    status,
  };
});

export const CLIENT_CLAIM_HISTORY = CLIENT_HISTORY.map((item) => ({
  id: `clm-${item.id}`,
  date: item.date.split('/').reverse().join('-'),
  accountId: item.accountId,
  token: item.token,
  tokenColor: item.tokenColor,
  amount: item.qty,
  valueUsd: item.debtPaid,
  status: item.status === 'claimed' ? ('claimed' as const) : ('expired' as const),
}));

export const CLIENT_KYC_CASES = kycCases;
export const CLIENT_USERS = users;

// ─── Lending Pools & Lender Data ─────────────────────────────────────────────

const [pools, lenderPositions] = await Promise.all([
  adapter.pools.list(),
  adapter.lenderPositions.listByUser('usr-011'),
]);

const poolById = new Map(pools.map((p) => [p.id, p]));

export type ClientLendingPool = {
  id: string;
  facilityId: string;
  name: string;
  deposited: string;
  lpTokens: string;
  apy: string;
  earnedYield: string;
  currentValue: string;
  tvl: string;
  utilization: string;
  withdrawable: string;
  withdrawablePct: number;
  depositedUsd: number;
  lpTokensRaw: number;
  earnedYieldUsd: number;
  currentValueUsd: number;
  apyPct: number;
};

export const CLIENT_LENDING_POOLS: ClientLendingPool[] = lenderPositions.map((pos) => {
  const pool = poolById.get(pos.poolId);
  const currentValue = pos.depositedUsd + pos.earnedYieldUsd;
  return {
    id: pool?.id ?? pos.poolId,
    facilityId: pool?.facilityId ?? '',
    name: pool?.name ?? pos.poolId,
    deposited: fmtCurrency(pos.depositedUsd),
    lpTokens: pos.lpTokens.toLocaleString(),
    apy: `${(pool?.apyPct ?? 0).toFixed(1)}%`,
    earnedYield: `+${fmtCurrency(pos.earnedYieldUsd)}`,
    currentValue: fmtCurrency(currentValue),
    tvl: `$${((pool?.tvlUsd ?? 0) / 1_000_000).toFixed(1)}M`,
    utilization: `${(pool?.utilizationPct ?? 0).toFixed(1)}%`,
    withdrawable: `${pos.withdrawablePct}%`,
    withdrawablePct: pos.withdrawablePct,
    depositedUsd: pos.depositedUsd,
    lpTokensRaw: pos.lpTokens,
    earnedYieldUsd: pos.earnedYieldUsd,
    currentValueUsd: currentValue,
    apyPct: pool?.apyPct ?? 0,
  };
});

export type ClientIOU = {
  id: string;
  poolId: string;
  facilityName: string;
  originalAmount: string;
  originalAmountUsd: number;
  repaidAmount: string;
  repaidAmountUsd: number;
  remainingAmount: string;
  remainingAmountUsd: number;
  status: 'outstanding' | 'partially_repaid' | 'settled';
};

export type ClientIOUEvent = {
  id: string;
  iouId: string;
  poolId: string;
  eventType: 'mint' | 'burn';
  amount: string;
  amountUsd: number;
  createdAt: string;
};

export type ClientYieldEntry = {
  id: string;
  week: string;
  yield: string;
  cumulative: string;
  yieldUsd: number;
  cumulativeUsd: number;
  order: number;
};

export async function getPoolDetail(poolId: string) {
  const pool = CLIENT_LENDING_POOLS.find((p) => p.id === poolId);
  const poolData = poolById.get(poolId);

  const [rawIous, rawIouEvents, rawYieldHistory] = await Promise.all([
    adapter.ious.listByPool(poolId),
    adapter.iouEvents.listByPool(poolId),
    adapter.yieldHistory.listByPool(poolId),
  ]);

  const ious: ClientIOU[] = rawIous.map((iou) => ({
    id: iou.id,
    poolId: iou.poolId,
    facilityName: iou.facilityName,
    originalAmount: fmtCurrency(iou.originalAmountUsd),
    originalAmountUsd: iou.originalAmountUsd,
    repaidAmount: fmtCurrency(iou.repaidAmountUsd),
    repaidAmountUsd: iou.repaidAmountUsd,
    remainingAmount: fmtCurrency(iou.remainingAmountUsd),
    remainingAmountUsd: iou.remainingAmountUsd,
    status: iou.status,
  }));

  const iouEvents: ClientIOUEvent[] = rawIouEvents.map((ev) => ({
    id: ev.id,
    iouId: ev.iouId,
    poolId: ev.poolId,
    eventType: ev.eventType,
    amount: fmtCurrency(ev.amountUsd),
    amountUsd: ev.amountUsd,
    createdAt: ev.createdAt,
  }));

  const yieldHistory: ClientYieldEntry[] = rawYieldHistory.map((entry, i) => ({
    id: entry.id,
    week: entry.weekLabel,
    yield: fmtCurrency(entry.yieldUsd),
    cumulative: fmtCurrency(entry.cumulativeUsd),
    yieldUsd: entry.yieldUsd,
    cumulativeUsd: entry.cumulativeUsd,
    order: i,
  }));

  return {
    pool,
    ious,
    iouEvents,
    yieldHistory,
    tvlUsd: poolData?.tvlUsd ?? 0,
    utilizationPct: poolData?.utilizationPct ?? 0,
  };
}
